import React from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Icon from '../AppIcon';
import { useLanguage } from 'hooks/useLanguage';

const Breadcrumb = ({ className = '' }) => {
  const { t } = useTranslation('common');
  const { isRTL } = useLanguage();
  const location = useLocation();
  const separatorIcon = isRTL ? 'ChevronLeft' : 'ChevronRight';

  const routeMap = {
    '/invoice-processing-queue': {
      label: t('navigation.processingQueue'),
      icon: 'Clock',
      description: t('navigation.processingQueueDescription')
    },
    '/invoice-error-correction': {
      label: t('navigation.errorCorrection'),
      icon: 'AlertTriangle',
      description: t('navigation.errorCorrectionDescription'),
      parent: '/invoice-processing-queue'
    },
    '/batch-processing-overview': {
      label: t('navigation.batchOverview'),
      icon: 'Layers',
      description: t('navigation.batchOverviewDescription')
    },
    '/processing-completion-summary': {
      label: t('navigation.completionSummary'),
      icon: 'CheckCircle',
      description: t('navigation.completionSummaryDescription'),
      parent: '/invoice-processing-queue'
    }
  };

  const currentRoute = routeMap?.[location?.pathname];
  
  if (!currentRoute) return null;

  const buildBreadcrumbPath = (path) => {
    const route = routeMap?.[path];
    if (!route) return [];
    
    const breadcrumbs = [];
    if (route?.parent) {
      breadcrumbs?.push(...buildBreadcrumbPath(route?.parent));
    }
    breadcrumbs?.push({ ...route, path });
    return breadcrumbs;
  };

  const breadcrumbs = buildBreadcrumbPath(location?.pathname);

  const handleNavigation = (path) => {
    if (path !== location?.pathname) {
      window.location.href = path;
    }
  };

  return (
    <nav className={`flex items-center gap-2 text-sm ${className}`} aria-label={t('breadcrumb.ariaLabel')}>
      {/* Home/Dashboard Link */}
      <button
        onClick={() => handleNavigation('/invoice-processing-queue')}
        className="flex items-center gap-1 text-text-secondary hover:text-foreground transition-colors duration-150"
        title={t('breadcrumb.returnTo', { label: t('navigation.processingQueue') })}
      >
        <Icon name="Home" size={14} color="currentColor" strokeWidth={2} />
        <span className="font-medium">{t('breadcrumb.dashboard')}</span>
      </button>
      {breadcrumbs?.length > 1 && (
        <>
          <Icon name={separatorIcon} size={14} color="currentColor" className="text-text-secondary" />
          
          {breadcrumbs?.slice(0, -1)?.map((crumb, index) => (
            <React.Fragment key={crumb?.path}>
              <button
                onClick={() => handleNavigation(crumb?.path)}
                className="flex items-center gap-1 text-text-secondary hover:text-foreground transition-colors duration-150"
                title={crumb?.description}
              >
                <Icon name={crumb?.icon} size={14} color="currentColor" strokeWidth={2} />
                <span className="font-medium">{crumb?.label}</span>
              </button>
              <Icon name={separatorIcon} size={14} color="currentColor" className="text-text-secondary" />
            </React.Fragment>
          ))}
        </>
      )}
      {/* Current Page */}
      <div className="flex items-center gap-1 text-foreground">
        <Icon name={currentRoute?.icon} size={14} color="currentColor" strokeWidth={2} />
        <span className="font-semibold">{currentRoute?.label}</span>
      </div>
    </nav>
  );
};

export default Breadcrumb;
